import React, { useEffect, useRef, useState, memo } from "react";
import { ReactComponent as GridViewIcon } from "assets/icons/grid_view.svg";
import { ReactComponent as ListViewIcon } from "assets/icons/view_list.svg";
import { ReactComponent as FormatListBulettedIcon } from "assets/icons/format_list_bulleted.svg";
import IconButton from "components/buttons/iconButton";
import Callout from "components/callout/callout";
import { useTranslation } from "react-i18next";
import { useSectionPad } from "components/layout/section";

export enum ViewMode {
    Grid = 'grid',
    List = 'list',
    Table = 'table'
}

interface DocListProps {
    filterElement?: React.ReactNode;
    defaultViewMode: ViewMode;
    id: string;
    children: React.ReactNode;
    viewModes: ViewMode[];
    noResultsElement?: React.ReactNode;
    showResults: boolean;
}

const DocList: React.FC<DocListProps> = ({
    filterElement,
    defaultViewMode,
    id,
    children,
    viewModes,
    noResultsElement,
    showResults
}) => {

    const { t } = useTranslation()
    const padX = useSectionPad()
    
    const [viewMode, setViewMode] = useState<ViewMode>(
        viewModes.includes(defaultViewMode) ? defaultViewMode : viewModes[0]
    );
    const [initialized, setInitialized] = useState(false);
    const viewModesRef = useRef(viewModes);
    viewModesRef.current = viewModes;

    useEffect(() => {
        const stored = localStorage.getItem(`docList:viewMode:${id}`);
        if (stored && viewModes.includes(stored as ViewMode)) {
            setViewMode(stored as ViewMode);
        } else {
            setViewMode((current) => (
                viewModes.includes(current)
                    ? current
                    : (viewModes.includes(defaultViewMode) ? defaultViewMode : viewModes[0])
            ));
        }
        setInitialized(true);
    }, [id, viewModes, defaultViewMode]);

    useEffect(() => {
        if (!initialized) return;
        const modes = viewModesRef.current;
        const stored = localStorage.getItem(`docList:viewMode:${id}`);
        if (stored && stored !== viewMode && !modes.includes(stored as ViewMode)) return;
        localStorage.setItem(`docList:viewMode:${id}`, viewMode as string);
    }, [viewMode, id, initialized]);

    const toggleViewMode = () => {
        const currentIdx = viewModes.indexOf(viewMode);
        const nextIdx = (currentIdx + 1) % viewModes.length;
        const next = viewModes[nextIdx];
        setViewMode(next);
        localStorage.setItem(`docList:viewMode:${id}`, next);
    };

    const getIcon = (mode: ViewMode) => {
        switch (mode) {
            case ViewMode.Grid:
                return <GridViewIcon />;
            case ViewMode.List:
                return <FormatListBulettedIcon />;
            case ViewMode.Table:
                return <ListViewIcon />;
            default:
                return null;
        }
    };

    return (
        <>
            <div className={`mb-2 w-full relative ${padX ? '' : 'px-2 lg:px-0'}`}>
                <div>{filterElement ?? null}</div>
                {viewModes.length > 1 && <div className={`absolute top-0 ${padX ? 'right-0' : 'right-2 lg:right-0'}`} onClick={toggleViewMode}>
                    <IconButton icon={getIcon(viewMode)} />
                </div>}
            </div>
            <div>
                {showResults ? React.Children.map(children, child => {
                    if (React.isValidElement(child)) {
                        const el = child as React.ReactElement<any>;
                        if (el.props && el.props["data-view-mode"] === viewMode) {
                            return el;
                        }
                    }
                    return null;
                }) : noResultsElement || <Callout type="error">{t('calloutNoResults')}</Callout>}
            </div>
        </>
    );
};

export default memo(DocList);
